import type {
	HourBucket,
	PlausibilityPrior,
	Season,
	VerificationScopeType,
} from "@sitescene/types";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";
import { queryKeys } from "@/api/query-keys";
import { useOrgSelection } from "@/context/use-org-selection";

export interface PriorWriteInput {
	classId: string;
	hourBucket?: HourBucket;
	logPriorOdds: number;
	notes?: string;
	season?: Season;
}

export interface TenantPriorWriteInput extends PriorWriteInput {
	scopeRef: string;
	scopeType: Exclude<VerificationScopeType, "global">;
}

/** Global prior rows (admin database). Not org-scoped. */
export const useGlobalPriors = () =>
	useQuery({
		queryFn: async () => {
			const response = await apiClient.get<PlausibilityPrior[]>(
				ENDPOINTS.shadow.plausibility.globalPriors,
			);
			return response.data;
		},
		queryKey: queryKeys.plausibility.globalPriors,
	});

/** Installation/zone/sensor-scoped prior rows of the selected organization. */
export const useTenantPriors = () => {
	const { selected } = useOrgSelection();
	const orgCode = selected?.orgCode ?? null;
	return useQuery({
		enabled: orgCode !== null,
		queryFn: async () => {
			const response = await apiClient.get<PlausibilityPrior[]>(
				ENDPOINTS.shadow.plausibility.priors,
			);
			return response.data;
		},
		queryKey: queryKeys.plausibility.priors(orgCode ?? ""),
	});
};

export const usePriorMutations = () => {
	const queryClient = useQueryClient();
	const { selected } = useOrgSelection();
	const orgCode = selected?.orgCode ?? "";

	const invalidateGlobal = () =>
		queryClient.invalidateQueries({
			queryKey: queryKeys.plausibility.globalPriors,
		});
	const invalidateTenant = () =>
		queryClient.invalidateQueries({
			queryKey: queryKeys.plausibility.priors(orgCode),
		});

	const upsertGlobal = useMutation({
		mutationFn: async (input: PriorWriteInput) =>
			(
				await apiClient.put<PlausibilityPrior>(
					ENDPOINTS.shadow.plausibility.globalPriors,
					input,
				)
			).data,
		onSuccess: invalidateGlobal,
	});

	const deleteGlobal = useMutation({
		mutationFn: async (priorId: string) =>
			(
				await apiClient.delete(
					ENDPOINTS.shadow.plausibility.globalPrior(priorId),
				)
			).data,
		onSuccess: invalidateGlobal,
	});

	const upsertTenant = useMutation({
		mutationFn: async (input: TenantPriorWriteInput) =>
			(
				await apiClient.put<PlausibilityPrior>(
					ENDPOINTS.shadow.plausibility.priors,
					input,
				)
			).data,
		onSuccess: invalidateTenant,
	});

	const deleteTenant = useMutation({
		mutationFn: async (priorId: string) =>
			(await apiClient.delete(ENDPOINTS.shadow.plausibility.prior(priorId)))
				.data,
		onSuccess: invalidateTenant,
	});

	return { deleteGlobal, deleteTenant, upsertGlobal, upsertTenant };
};
